export const translations = {
  // Map bot-verification gate
  mapGate: {
    blocked: "We couldn't verify your browser, so the map didn't load.",
    slow: 'Still checking your browser. Complete the check above to load the map.',
    retry: 'Try again',
  },
  // Rating labels
  ratings: {
    terrible: 'Terrible',
    poor: 'Poor',
    okay: 'Okay',
    good: 'Good',
    excellent: 'Excellent',
  },
  // Navigation and UI
  navigation: {
    home: 'Home',
    about: 'About',
    faq: 'FAQ',
    howTo: 'How to Check',
    privacy: 'Privacy Policy',
    search: 'Search restaurants...',
    languageSwitch: 'Switch to Dutch',
    reportPoisoning: 'Report Food Poisoning',
    guide: 'Food Safety Guide',
    rights: 'Consumer Rights',
    symptoms: 'Symptoms Guide',
    navigation: 'Navigation',
    features: 'Features',
    chains: 'Chains',
    chainsCompared: 'Chains compared',
    explore: 'Explore',
    inspectionBacklog: 'Inspection backlog',
    clickToCollapse: 'Click to collapse',
    clickToExpand: 'Click to expand',
    copyright: '© 2026 Kauwen of Spauwen. All rights reserved.'
  },

  // Homepage content
  homepage: {
    title: 'Check Restaurant Hygiene Before You Eat',
    subtitle: 'Food Safety Ratings & Hygiene Inspection Results',
    description: 'Check restaurant hygiene scores before dining out. View food safety inspections, read community reports, and make informed choices. Report food poisoning anonymously.',
    searchPlaceholder: 'Search a restaurant to check hygiene...',
    welcomeMessage: 'Check your restaurant before you eat',
    featuresTitle: 'Make Safer Dining Choices',
    anonymousReporting: 'Report food poisoning anonymously - no registration needed',
    officialData: 'View official hygiene inspection results',
    realTimeUpdates: 'Real-time food safety reports from other diners',
    ctaReport: 'Report Food Poisoning',
    ctaCheck: 'Check Restaurant Hygiene',
    recentReports: 'Recent Food Safety Reports',
    worstRated: 'Restaurants to Avoid - Poor Hygiene Scores'
  },

  // Restaurant details
  restaurant: {
    details: 'Restaurant Details',
    address: 'Address',
    inspectionRating: 'Inspection Rating',
    lastInspection: 'Last Inspection',
    operatorInfo: 'Operator Information',
    reportIssue: 'Report Food Safety Issue',
    viewOnMap: 'View on Map',
    safetyScore: 'Food Safety Score',
    hygieneRating: 'Hygiene Rating',
    inspectionHistory: 'Inspection History',
    noInspectionData: 'No inspection data available',
    ratingExcellent: 'Excellent',
    ratingGood: 'Good',
    ratingFair: 'Fair',
    ratingPoor: 'Poor',
    ratingUnknown: 'Unknown',
    share: 'Share',
    close: 'Close',
    copied: 'Copied!',
    shareTitle: '{{name}} - Kauwen of Spauwen',
    shareText: 'Check out {{name}} on Kauwen of Spauwen!',
    detailsAndHistory: 'Details & History',
    expandDetails: 'Show details',
    collapseDetails: 'Hide details',
    loadingDetails: 'Loading restaurant details...',
    userReports: 'User Reports',
    noReportsYet: 'No reports yet for this restaurant',
    beFirstToReport: 'Be the first to add a report!',
    addReport: 'Add a report',
    tryAgain: 'Try Again',
    noScoreChange: 'No change in score',
    inspectionSource: {
      title: 'Inspection Data Source',
      description: 'The inspection results displayed here come from the official Belgian food safety database maintained by the Federal Agency for the Safety of the Food Chain (FASFC).',
      moreInfo: 'For more detailed information about this restaurant\'s inspection history, you can',
      viewOnFoodweb: 'view it on Foodweb',
      info: 'Information about inspection data source'
    }
  },

  // Search and filters
  search: {
    placeholder: 'Search restaurants, cities, or addresses...',
    noResults: 'No restaurants found',
    searching: 'Searching...',
    filterByRating: 'Filter by Safety Rating',
    showAll: 'Show All',
    nearMe: 'Near Me',
    recentReports: 'Recent Reports',
    clearFilters: 'Clear Filters',
    enterRestaurantName: 'Enter restaurant name...',
    loading: 'Loading...',
    noResultsFound: 'No results found.',
    recentlyViewed: 'Recently viewed',
    clearRecent: 'Clear',
    emojiMeaningsQuestion: 'What do these emojis mean?'
  },

  // Forms and reporting
  forms: {
    reportTitle: 'Report Food Poisoning',
    restaurantName: 'Restaurant Name',
    issueDescription: 'Describe your food poisoning experience',
    dateOfVisit: 'When did you eat there?',
    symptoms: 'Food Poisoning Symptoms',
    submit: 'Submit Food Poisoning Report',
    submitting: 'Submitting report...',
    thankYou: 'Thank you for reporting food poisoning',
    required: 'Required field',
    optional: 'Optional',
    anonymous: 'Your food poisoning report is 100% anonymous',
    symptomsList: {
      nausea: 'Nausea',
      vomiting: 'Vomiting',
      diarrhea: 'Diarrhea',
      fever: 'Fever',
      cramps: 'Stomach cramps',
      headache: 'Headache'
    },
    whenSick: 'When did you get sick?',
    whatAte: 'What did you eat?',
    howMany: 'How many people got sick?'
  },

  // About page content
  about: {
    title: 'About Our Community Food Safety Platform',
    subtitle: 'Anonymous food poisoning reporting to protect public health',
    missionTitle: 'Our Mission',
    missionText: 'We are a community-driven platform dedicated to preventing food poisoning. By allowing consumers to anonymously report their negative experiences at restaurants, we create a collaborative database that helps everyone make safer dining choices. Our system provides real-time insights based on actual customer experiences.',
    howItWorksTitle: 'How It Works',
    step1Title: 'Anonymous Reporting',
    step1Text: 'Report food poisoning without creating an account. Your identity remains completely confidential.',
    step2Title: 'Community Validation',
    step2Text: 'Multiple reports from the same establishment create a pattern of warning for other diners.',
    step3Title: 'Active Prevention',
    step3Text: 'Check recent reports and hygiene scores before choosing where to eat.',
    dataSourcesTitle: 'Our Data Sources',
    dataSourcesText: 'We combine two types of information to give you a complete view of food safety:',
    privacyTitle: 'Your Privacy Is Our Priority',
    privacyText: 'All reports are 100% anonymous. We collect no personally identifiable information. Your report helps the community while protecting your identity.',
    whyImportantTitle: 'Why This Matters',
    whyImportantText: 'Food poisoning affects thousands of people each year. Many cases go unreported to official authorities. Our platform enables quick and easy reporting that can prevent further cases and encourage restaurants to maintain high hygiene standards.',
    communityImpactTitle: 'Community Impact',
    stat1: 'Anonymous reports processed',
    stat2: 'Restaurants monitored',
    stat3: 'Monthly active users',
    joinUsTitle: 'Join Our Community',
    joinUsText: 'Together, we can create a safer dining environment for everyone.'
  },

  // FAQ content
  faq: {
    title: 'Food Poisoning & Restaurant Hygiene FAQs',
    subtitle: 'Common questions about reporting food poisoning',
    q1: 'How do I report food poisoning?',
    a1: 'You can report food poisoning anonymously on our platform by finding the restaurant and submitting a report. This helps warn other diners immediately.',
    q2: 'What are symptoms of food poisoning?',
    a2: 'Common food poisoning symptoms include nausea, vomiting, diarrhea, stomach cramps, fever, and headache. Symptoms usually start 2-6 hours after eating contaminated food.',
    q3: 'How does this platform work?',
    a3: 'Our platform offers fast, anonymous food poisoning reporting. Reports are visible immediately to help others avoid getting sick.',
    q4: 'How long does food poisoning last?',
    a4: 'Most food poisoning cases last 1-3 days. Seek medical attention if symptoms persist longer, you have high fever, blood in stool, or signs of dehydration.',
    q5: 'Can I see which restaurants have poor hygiene?',
    a5: 'Yes, we display hygiene inspection scores and food poisoning reports. Look for restaurants with poor ratings or recent food poisoning reports.',
    q6: 'What causes food poisoning in restaurants?',
    a6: 'Common causes include poor hygiene, improper food storage, cross-contamination, undercooked food, and sick food handlers. Our reports help identify these issues.',
    q7: 'What should I do in serious cases?',
    a7: 'For serious cases, seek medical attention immediately. Our platform provides immediate visibility to help other diners make informed decisions.'
  },

  // How-to guide content
  howTo: {
    title: 'How to Check Restaurant Food Safety',
    subtitle: 'A comprehensive guide to evaluating restaurant hygiene and safety',
    introText: 'Making informed decisions about where to dine is crucial for your health and safety. Here\'s how to use our platform and what to look for.',
    step1Title: 'Check Official Ratings',
    step1Text: 'Start by looking at the inspection rating. This reflects the restaurant\'s compliance with hygiene standards.',
    step2Title: 'Review Inspection History',
    step2Text: 'Look at the inspection history to see trends over time. Recent improvements or recurring issues can provide valuable insights.',
    step3Title: 'Read Community Reports',
    step3Text: 'Anonymous community reports can provide real-world experiences and recent concerns that may not yet be reflected in official inspections.',
    step4Title: 'Consider Location and Type',
    step4Text: 'Different types of establishments may have different risk profiles. Consider the cuisine type and preparation methods.',
    warningSignsTitle: 'Warning Signs to Watch For',
    warningSign1: 'Multiple recent negative reports',
    warningSign2: 'Declining inspection scores over time',
    warningSign3: 'Poor hygiene ratings',
    warningSign4: 'Reports of foodborne illness',
    tipsTitle: 'Additional Safety Tips',
    tip1: 'Trust your instincts - if something seems off, consider dining elsewhere',
    tip2: 'Check if the restaurant displays their hygiene rating prominently',
    tip3: 'Look for signs of cleanliness in visible areas',
    tip4: 'Consider the freshness and temperature of food when served'
  },

  // Privacy policy content
  privacy: {
    title: 'Privacy Policy',
    subtitle: 'How we protect your privacy and handle data',
    lastUpdated: 'Last updated: {{date}}',
    introTitle: 'Our Commitment to Privacy',
    introText: 'We are committed to protecting your privacy and ensuring that all reports remain completely anonymous.',
    dataCollectionTitle: 'Data Collection',
    dataCollectionText: 'We collect only the minimum information necessary to operate our platform. For anonymous reports, we do not collect any personally identifiable information.',
    anonymityTitle: 'Anonymity Guarantee',
    anonymityText: 'All food safety reports are completely anonymous. We do not track IP addresses, require accounts, or collect any information that could identify you.',
    dataUseTitle: 'How We Use Data',
    dataUseText: 'Data is used solely to provide food safety information to the public. We do not sell, share, or monetize user data.',
    cookiesTitle: 'Cookies and Tracking',
    cookiesText: 'We use minimal cookies for essential functionality only. We do not use tracking cookies or analytics that could compromise anonymity.',
    contactTitle: 'Contact Us',
    contactText: 'If you have questions about this privacy policy, please contact us through our platform.'
  },

  // Common UI elements
  common: {
    loading: 'Loading...',
    error: 'An error occurred',
    retry: 'Retry',
    search: 'Search...',
    close: 'Close',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    view: 'View',
    share: 'Share',
    copy: 'Copy',
    copied: 'Copied!',
    showMore: 'Show More',
    showLess: 'Show Less',
    readMore: 'Read More',
    readLess: 'Read Less',
    tryAgain: 'Try Again',
    page: 'Page',
    of: 'of',
    perPage: 'per page',
    all: 'All',
    noResults: 'No results found',
    locateMe: 'Locate me',
    toggleMenu: 'Toggle menu',
    loadingRestaurant: 'Loading restaurant...',
    loadingDetails: 'Loading...',
    addYourReport: 'Add Your Report',
    words: 'words',
    star: 'star',
    stars: 'stars'
  },

  // Reports section
  reports: {
    loading: 'Loading recent reports...',
    error: 'Failed to load reports',
    noReports: 'No recent reports available',
    noReportsSubtext: 'Reports will appear here as they are submitted',
    latestReports: 'Latest Food Safety Reports',
    reports: 'Reports',
    summary: 'Recent Activity Summary',
    totalReports: 'Total Reports',
    avgRating: 'Avg Rating',
    activeRestaurants: 'Recent Restaurants',
    activeCities: 'Active Cities',
    cta: 'Have you experienced food safety issues? Share your experience to help others make informed dining decisions.'
  },

  // Navigation keys
  nav: {
    home: 'Home',
    about: 'About',
    faq: 'FAQ',
    howToCheck: 'How to Check',
    privacy: 'Privacy Policy'
  },

  // SEO and meta content
  seo: {
    keywords: {
      foodSafety: 'food safety',
      restaurantHygiene: 'restaurant hygiene',
      foodPoisoning: 'food poisoning',
      restaurantRatings: 'restaurant ratings',
      foodInspection: 'food inspection',
      restaurants: 'restaurants',
      foodQuality: 'food quality',
      restaurantReviews: 'restaurant reviews',
      anonymousReporting: 'anonymous reporting',
      hygieneRating: 'hygiene rating'
    }
  },

  // Emoji meanings
  emoji: {
    meanings: {
      title: 'Emoji Meanings',
      skull: 'Bad inspection twice with police report',
      sick: 'Failed inspection with warning, awaiting reinspection',
      slight_smile: 'Bad inspection but fixed issues after reinspection',
      smile: 'Passed last inspection',
      heart_eyes: 'Validated by external company',
      question: 'Unknown status or no data'
    },
    selectEmoji: 'Select emoji: {{emoji}}'
  },

  // Wall of Shame
  wallOfShame: {
    title: 'Wall of Shame',
    subtitle: 'Displaying establishments with the poorest food safety inspection results.',
    foundCount: '{{count}} establishments found with "Te verbeteren" rating',
    showingPage: 'Showing page',
    noEstablishments: 'No establishments with poor ratings found.',
    columns: {
      id: 'ID',
      name: 'Name',
      address: 'Address',
      inspectionDate: 'Inspection Date',
      details: 'Details'
    }
  },

  // Data Table
  dataTable: {
    title: 'Data Table',
    filterByStatus: 'Filter by Status:',
    columns: {
      id: 'ID',
      name: 'Name',
      address: 'Address',
      status: 'Status',
      tooltip: 'Tooltip',
      inspectionResult: 'Inspectie resultaat'
    },
    statuses: {
      toImprove: 'Te verbeteren',
      good: 'Goed',
      veryGood: 'Zeer goed'
    }
  },

  // Report Form
  reportForm: {
    title: 'Create New Report',
    createNewReport: 'Create New Report',
    successMessage: 'Report submitted successfully!',
    shareYourReport: 'Share your report',
    done: 'Done',
    anonymousUsername: 'Your Anonymous Username',
    privacyNote: 'For privacy, we generate a random username for each report',
    yourReport: 'Your Report',
    placeholder: 'Share your experience with this restaurant...',
    wordCount: '{{count}} / {{max}} words',
    wordLimitExceeded: 'Please keep your report under {{limit}} words',
    rateExperience: 'Rate Your Experience',
    selectRating: 'Select a rating',
    selectedRating: 'You rated this restaurant {{rating}} out of 5 stars',
    youRated: 'You rated this restaurant',
    submitting: 'Submitting...',
    addReport: 'Add Report',
    cancel: 'Cancel',
    addImage: 'Add Image (Optional)',
    clickToUpload: 'Click to upload',
    imageRequirements: 'PNG, JPG up to 15MB',
    imageTooLarge: 'Image size must be less than {{size}}',
    invalidImageType: 'Please upload a valid image file',
    imageLoadError: 'Failed to load image',
    loadingImage: 'Loading image...',
    detailsOptional: 'Details (Optional)',
    symptomsLabel: 'Symptoms',
    visitDateLabel: 'Date of visit',
    dishOrderedLabel: 'Dish ordered',
    dishOrderedPlaceholder: 'e.g. pasta, sushi...'
  },

  // Time formatting
  time: {
    justNow: 'just now',
    minuteAgo: '1 minute ago',
    minutesAgo: '{{count}} minutes ago',
    hourAgo: '1 hour ago',
    hoursAgo: '{{count}} hours ago',
    dayAgo: '1 day ago',
    daysAgo: '{{count}} days ago',
    weekAgo: '1 week ago',
    weeksAgo: '{{count}} weeks ago',
    monthAgo: '1 month ago',
    monthsAgo: '{{count}} months ago',
    yearAgo: '1 year ago',
    yearsAgo: '{{count}} years ago'
  },

  // Restaurant History
  restaurantHistory: {
    passedInspection: 'Passed inspection',
    receivedWarning: 'Received a warning',
    inspectionTooOld: 'Inspection is too old',
    awaitingInspection: 'Awaiting new inspection',
    receivedFine: 'Received a fine',
    receivedPV: 'Received a PV'
  },

  // Recent Reports
  recentReports: {
    title: 'Recent Reports',
    ariaLabel: 'Recent Reports'
  },

  // Sidebar
  sidebar: {
    recentReports: 'Recent Reports',
    trends: 'Trending'
  },

  // Trends
  trends: {
    title: 'Trending Restaurants',
    loading: 'Loading trends...',
    error: 'Failed to load trends',
    noTrends: 'No trending data available',
    noTrendsSubtext: 'Trend data will appear here when available',
    cityNoTrends: 'No recent score changes in {{city}}',
    cityNoTrendsSubtext: 'Inspections here have kept the same score recently',
    showNationalTrends: 'Show trends for all of Belgium',
    noUpwardTrends: 'No upward trends available',
    noDownwardTrends: 'No downward trends available',
    restaurants: 'restaurants',
    summary: 'Trend Summary',
    improving: 'Improving',
    declining: 'Declining',
    popular: 'Popular',
    new: 'New',
    reports: 'reports',
    points: 'points',
    lastUpdated: 'Last updated',
    cta: 'Click on a restaurant to view details and inspection history.',
    significantImprovements: '{{count}} significant improvements',
    significantDeclines: '{{count}} significant declines',
    avgPoints: 'Avg: {{value}} points',
    noResultsFiltered: 'No results',
    minChange: 'Min change:',
    all: 'All',
    sortByChange: 'Change',
    sortByName: 'Name',
    sortByDate: 'Date',
    significantChanges: 'Big changes',
    recentInspections: 'Last 30 days',
    bigChanges: 'Big changes only',
    mostRecent: 'Most recent',
    daysAgo: 'days ago'
  },

  // Comparison badges
  comparison: {
    whyThisScore: 'Why this score?',
    aboveAverage: 'above average',
    belowAverage: 'below average',
    verdict: {
      topCategory: 'The highest-rated {{category}} in {{area}}.',
      topPostal: 'The highest-rated restaurant in {{postalCode}}.',
      topCity: 'The highest-rated restaurant in {{cityName}}.',
      topPercentile: 'Top {{percentile}}% in {{cityName}}.',
      ranked: 'Ranked #{{rank}} among {{total}} in {{area}}, scoring {{diff}} points {{aboveBelow}}.'
    },
    breakdown: {
      history: 'History',
      forks: 'Official Rating',
      reports: 'User Reports',
      officialRating: 'official rating',
      basedOnReports: 'Based on {{count}} user reports',
      noHistory: 'No inspection history available',
      historyWarning: 'Has warnings or fines in history',
      historyStrong: 'Consistently passes inspections',
      historyNeutral: 'Mixed inspection history'
    },
    labels: {
      city: 'City',
      area: 'Area',
      category: 'Category'
    },
    betterThan: 'Better than {{percent}}%',
    inWorst: 'In the worst {{percent}}%',
    inBest: 'In the best {{percent}}%',
    quality: {
      excellent: 'Excellent',
      good: 'Good',
      average: 'Average',
      belowAvg: 'Below avg'
    },
    historyStatus: {
      noData: 'No data',
      hasIssues: 'Has issues',
      clean: 'Clean',
      mixed: 'Mixed'
    }
  },

  // Badge embed
  badge: {
    triggerLink: 'Your business?',
    modalTitle: 'Showcase Your Rating',
    modalDescription: 'Proud of your hygiene score? Display it on your website or social media to build trust with customers.',
    embedLabel: 'Embed on your website',
    imageUrlLabel: 'Or share the image URL',
    preview: 'Badge Preview',
    tabs: {
      embed: 'Embed Code',
      showcase: 'QR Showcase'
    },
    showcase: {
      scanToVerify: 'Scan to verify',
      downloadPng: 'Download PNG',
      downloading: 'Generating...',
      printTip: 'Optimized for print at 300 DPI',
      forks: '{{count}} Forks',
      ratingLabel: {
        '5': 'Excellent',
        '4': 'Very Good',
        '3': 'Good',
        '2': 'Poor',
        '1': 'Critical'
      }
    }
  },

  // Subscription
  subscription: {
    notifyMe: 'Notify me of rating changes',
    title: 'Stay Updated',
    description: 'Get notified when the inspection rating changes for {{restaurantName}}',
    emailPlaceholder: 'Enter your email address',
    subscribe: 'Subscribe',
    subscribing: 'Subscribing...',
    success: 'Successfully subscribed!',
    privacy: 'We\'ll only use your email to send rating updates for this restaurant.',
    invalidEmail: 'Please enter a valid email address',
    alreadySubscribed: 'You are already subscribed to updates for this restaurant',
    error: 'Failed to subscribe. Please try again later.',
    close: 'Close',
    onePersonWatching: '1 person watching',
    peopleWatching: '{{count}} people watching'
  },

  city: {
    prose: {
      overview: '{{city}} has {{count}} inspected restaurants with an average hygiene score of {{avg}}/5.',
      reports: '{{count}} food safety reports have been filed by diners in {{city}}.',
      topPick: 'Top rated: {{name}}.',
      warning: '{{count}} restaurants flagged for poor hygiene.'
    }
  },

  province: {
    prose: {
      overview: '{{province}} has {{count}} inspected restaurants with an average hygiene score of {{avg}}/5.',
      reports: '{{count}} food safety reports have been filed in {{province}}.',
      topPick: 'Top rated: {{name}}.'
    }
  },

  chain: {
    sectionTitle: 'Part of a chain?',
    sectionBody: '{{count}} locations share this name in our database.',
    sectionAvg: 'Average FAVV score across all locations: {{avg}}/5',
    sectionLink: 'View all locations',
    pageSubtitle: 'Locations sharing this name in Belgium',
    avgExplain: 'average FAVV score across {{count}} inspected locations',
    scoredPct: '{{pct}}% of locations have a recent inspection score',
    locations: 'Locations ({{count}})',
    sortCity: 'By area',
    sortScoreAsc: 'Score: low → high',
    sortScoreDesc: 'Score: high → low',
    distribution: 'Score distribution',
    unscored: '{{count}} locations without a recent score',
    share: 'Share',
    shareText: '{{name}}: average FAVV score {{avg}}/5 across {{count}} locations',
    methodology: 'Averages are based on official FAVV inspection results per location. Locations are grouped by name and may belong to different operators.',
    noData: 'No data found for this chain',
    prose: {
      overview: '{{name}} has {{count}} locations in our database, {{scored}} of which have a recent FAVV inspection score. The average score is {{avg}} out of 5.'
    },
    rankings: {
      title: 'Chains compared',
      subtitle: 'Average FAVV inspection scores per chain',
      intro: 'Compare supermarket, fast food and retail chains by the average official FAVV inspection score of their locations.',
      sortBySize: 'Locations',
      sortByScoreDesc: 'Score: high → low',
      sortByScoreAsc: 'Score: low → high',
      minSize: 'Min. locations',
      locationCount: '{{count}} locations',
      noData: 'No chain data available yet'
    }
  },

  backlog: {
    title: 'Inspection backlog',
    subtitle: 'Active establishments whose last FAVV inspection was unfavorable and at least 2 years ago',
    statExplain: 'establishments with an unfavorable score and no inspection result in 2+ years',
    oldest: 'Oldest unfavorable result: {{date}}',
    allProvinces: 'All',
    lastInspection: 'Last inspection {{date}} · {{years}} years ago',
    loadMore: 'Show more ({{shown}}/{{total}})',
    noData: 'No data available',
    methodology: 'Based on public FAVV inspection results. An old score says nothing about the current situation; there is simply no more recent result available in the public data.'
  }
};

export default translations;
